import { Menu, MenuProps } from 'antd';
import lodash from 'lodash';
import React, { useEffect, useState } from 'react';
import * as Icon from 'react-feather';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { NavigateFunction, useLocation, useNavigate } from 'react-router-dom';

import { CheckPermissionFunc } from '@hoc/CheckPermission';
import { RootState } from '@modules';
import { OrganizationsSelector } from '@modules/organizations/organizationStore';
import { IRouter } from '@routers/interface';

interface IRenderMenuProps {
  listNav: Array<IRouter>;
  location: string;
  collapse?: boolean;
}

const renderMenuItem = (
  data: IRouter,
  facilityId?: string,
  listPermissionCode?: string[],
  children?: MenuProps['items'],
  type?: string,
  navigate?: NavigateFunction,
) => {
  let path = data.path;
  if (data.menu?.generatePath) {
    path = data.menu.generatePath(facilityId, listPermissionCode);
  } else if (data.generatePath) {
    path = data.generatePath(facilityId, listPermissionCode);
  }
  return {
    key: data.menu?.activePath || data.activePath || data.path,
    label: (
      <div className="class-border">
        <div className={'item-label'}>
          <FormattedMessage id={data.name} defaultMessage={data.name} />
        </div>
      </div>
    ),
    icon: data.menu?.icon || data.icon,
    children: children,
    onClick: () => {
      if (path != null) navigate?.(path);
    },
    type: type,
  };
};

const MenuCustom: React.FC<IRenderMenuProps> = (props: IRenderMenuProps) => {
  const listNav = props.listNav;
  const { listPermissionCode, user } = useSelector((state: RootState) => state.profile);
  const organization = useSelector(OrganizationsSelector);
  const [selectedKeys, setSelectedKeys] = useState<string[]>();
  const location = useLocation();
  const navigate = useNavigate();
  const [items, setItems] = useState<MenuProps['items']>([]);

  const isDivider = (it?: any) => it?.type === 'divider';

  const normalizeDividers = (_items: any[]) => {
    const arr = _items ? _items.filter(Boolean) : [];

    const tmp: any[] = [];
    for (const it of arr) {
      if (isDivider(it)) {
        if (tmp.length === 0) {
          continue;
        }
        if (isDivider(tmp[tmp.length - 1])) {
          continue;
        }
      }
      tmp.push(it);
    }

    while (tmp.length && isDivider(tmp[tmp.length - 1])) {
      tmp.pop();
    }

    let d = 0;
    return [...tmp].map((it: any) => {
      const _it = lodash.cloneDeep(it);

      if (isDivider(_it)) {
        d += 1;
        return { ..._it, key: _it?.key ?? `divider-${d}` };
      }

      return _it;
    });
  };

  const handleChangeMenu = e => {
    setSelectedKeys(e.selectedKeys);
    sessionStorage.clear();
  };

  useEffect(() => {
    const newItem: any[] = [];

    listNav?.forEach((item: IRouter) => {
      if (item?.type === 'divider') {
        newItem.push({ type: 'divider', key: `divider-${newItem.length}` });
        return;
      }

      const ok =
        item?.menu != null &&
        item?.menu?.hideInNavbar !== true &&
        CheckPermissionFunc(item?.permissionCode, listPermissionCode);

      if (!ok) {
        return;
      }

      if (item?.routes && !lodash.isEmpty(item?.routes)) {
        const children: any[] = [];

        item?.routes?.forEach((route: IRouter) => {
          const okChild =
            route?.menu?.hideInNavbar !== true &&
            CheckPermissionFunc(route?.permissionCode, listPermissionCode);

          if (!okChild) {
            return;
          }

          children.push(
            renderMenuItem(
              route,
              user?.facility?.id || organization?.id,
              listPermissionCode,
              undefined,
              route?.type,
              navigate,
            ),
          );
        });

        if (item.path == null && children.length === 0) {
          return;
        }

        newItem.push(
          renderMenuItem(
            item,
            user?.facility?.id || organization?.id,
            listPermissionCode,
            children.length ? children : undefined,
            item?.type,
            navigate,
          ),
        );
        return;
      }

      newItem.push(
        renderMenuItem(
          item,
          user?.facility?.id || organization?.id,
          listPermissionCode,
          undefined,
          item?.type,
          navigate,
        ),
      );
    });

    setItems(normalizeDividers(newItem));
  }, [listNav, listPermissionCode, user?.facility?.id, organization?.id]);

  useEffect(() => {
    if (!items || items.length === 0) {
      return;
    }

    let bestKey: string | null = null;

    items?.forEach((item: any) => {
      if (!item || isDivider(item)) {
        return;
      }

      if (item?.children?.length) {
        item?.children?.forEach((child: any) => {
          const ck = String(child?.key ?? '');
          if (!ck) {
            return;
          }

          if (location.pathname.includes(ck)) {
            if (!bestKey || ck.length > bestKey.length) {
              bestKey = ck;
            }
          }
        });
      } else {
        const k = String(item?.key ?? '');
        if (!k) {
          return;
        }

        if (location.pathname.includes(k)) {
          if (!bestKey || k.length > bestKey.length) {
            bestKey = k;
          }
        }
      }
    });

    if (bestKey) {
      setSelectedKeys([bestKey]);
    } else {
      setSelectedKeys([]);
    }
  }, [location.pathname, items]);

  return (
    <Menu
      defaultSelectedKeys={selectedKeys}
      selectedKeys={selectedKeys}
      onSelect={handleChangeMenu}
      mode="vertical"
      items={items}
      expandIcon={<Icon.MoreVertical />}
    />
  );
};

export default MenuCustom;
